import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAddSessionCredits } from "@/lib/sessionQueries";

const SOURCE_OPTIONS = [
  { value: "programme", label: "Included in programme" },
  { value: "extra", label: "Extra session" },
];
const TYPE_LABELS = { individual: "Individual session", group: "Group session" };

/**
 * Master v5 Prompt 30G — Client 360 -> Sessions -> "+ Add sessions".
 * Adds entitlement/credits only: it never creates or schedules a
 * Session (that's Propose Session) and never spends a credit (that's
 * Book session) — see session_repository.grant_entitlement_batch.
 */
export default function AddSessionsModal({ workspaceId, memberId, accessType, workspaceMembers, onClose, onSuccess, nested }) {
  const isIndividualWorkspace = accessType !== "team" && workspaceMembers.length === 1;
  const [quantity, setQuantity] = useState(1);
  const [creditSource, setCreditSource] = useState("programme");
  const [sessionType, setSessionType] = useState("individual");
  const [participantIds, setParticipantIds] = useState(isIndividualWorkspace ? [memberId] : memberId ? [memberId] : []);
  const addCredits = useAddSessionCredits(workspaceId);

  // Individual mode only ever holds one participant — trim silently if
  // the coach switches Group -> Individual with more than one selected.
  useEffect(() => {
    if (sessionType === "individual" && participantIds.length > 1) {
      setParticipantIds((prev) => prev.slice(0, 1));
    }
  }, [sessionType, participantIds.length]);

  function toggleParticipant(id) {
    if (sessionType === "individual") {
      setParticipantIds([id]);
      return;
    }
    setParticipantIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }

  const selectedNames = workspaceMembers.filter((m) => participantIds.includes(m.membership_id)).map((m) => m.display_name);
  const canSubmit = quantity >= 1 && participantIds.length >= 1 && (sessionType !== "individual" || participantIds.length === 1);

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await addCredits.mutateAsync({
        quantity: Number(quantity),
        credit_source: creditSource,
        session_type: sessionType,
        participant_member_ids: participantIds,
      });
      toast.success(`Added ${quantity} session${quantity === 1 ? "" : "s"}`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't add sessions");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={nested ? "max-w-lg !z-[100]" : "max-w-lg"}
        overlayClassName={nested ? "!z-[90]" : undefined}
        data-testid="add-sessions-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-serif">Add sessions</DialogTitle>
          <DialogDescription>
            This adds session credits/entitlement only — it doesn't create or schedule a Session.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-ink-soft uppercase mb-1.5 block">Number of sessions</label>
            <Input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
              data-testid="add-sessions-quantity-input"
            />
          </div>

          <div>
            <label className="text-xs text-ink-soft uppercase mb-1.5 block">Session source</label>
            <Select value={creditSource} onValueChange={setCreditSource}>
              <SelectTrigger data-testid="add-sessions-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`add-sessions-source-${o.value}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs text-ink-soft uppercase mb-1.5 block">Session type</label>
            <Select value={sessionType} onValueChange={setSessionType}>
              <SelectTrigger data-testid="add-sessions-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="individual" data-testid="add-sessions-type-individual">Individual</SelectItem>
                <SelectItem value="group" data-testid="add-sessions-type-group">Group</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs text-ink-soft uppercase mb-1.5 block">
              Participants {sessionType === "group" && <span className="text-ink-soft/70">(select one or more)</span>}
            </label>
            <div className="grid grid-cols-2 gap-2" data-testid="add-sessions-participants-grid">
              {workspaceMembers.map((m) => {
                const selected = participantIds.includes(m.membership_id);
                return (
                  <button
                    key={m.membership_id}
                    type="button"
                    onClick={() => toggleParticipant(m.membership_id)}
                    className={`flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-sm text-left transition-colors ${
                      selected ? "border-terracotta bg-terracotta/10 text-ink" : "border-ink/10 text-ink-soft hover:border-ink/20"
                    }`}
                    data-testid={`add-sessions-participant-${m.membership_id}`}
                  >
                    <span>{m.display_name}</span>
                    {selected && <Check size={14} className="text-terracotta shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border border-ink/10 rounded-lg p-3 bg-white/50 space-y-1" data-testid="add-sessions-summary">
            <p className="text-sm text-ink font-medium">
              Add {quantity || 0} session{Number(quantity) === 1 ? "" : "s"}
            </p>
            <p className="text-xs text-ink-soft">{SOURCE_OPTIONS.find((o) => o.value === creditSource)?.label}</p>
            <p className="text-xs text-ink-soft">{TYPE_LABELS[sessionType]}</p>
            <p className="text-xs text-ink-soft">
              Participants: {selectedNames.length > 0 ? selectedNames.join(", ") : "None selected"}
            </p>
          </div>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="text-sm text-ink/60 px-4 py-2" data-testid="add-sessions-cancel-btn">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || addCredits.isPending}
            className="bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
            data-testid="add-sessions-submit-btn"
          >
            {addCredits.isPending ? "Adding…" : `Add ${quantity || 0} session${Number(quantity) === 1 ? "" : "s"}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
